import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import { initialProducts } from '../data/mockData';

const StoreContext = createContext();

export const useStore = () => useContext(StoreContext);

const KEEP_ALIVE_KEY = 'ceylanthro_last_ping';
const KEEP_ALIVE_INTERVAL = 3 * 24 * 60 * 60 * 1000; // 3 days in ms
const SESSION_KEY = 'ceylanthro_session_id';

const DEFAULT_LOADING_ICON = 'https://cloud.sharemyimage.com/2026/08/05/whitemotionvid-837d33ed-2540-4e3f-ae05-b9b121fb299c.gif';

export const StoreProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [landingSettings, setLandingSettings] = useState([]);
  const [appSettings, setAppSettings] = useState([]);
  const [shippingDistricts, setShippingDistricts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
    fetchLandingSettings();
    fetchAppSettings();
    fetchShippingDistricts();
    checkUser();
    startKeepAlive();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user || null;
      setUser(currentUser);
      if (currentUser) {
        fetchProfile(currentUser.id);
      } else {
        setProfile(null);
      }
    });

    const savedCart = localStorage.getItem('ceylanthro_cart');
    if (savedCart) setCart(JSON.parse(savedCart));

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('ceylanthro_cart', JSON.stringify(cart));
  }, [cart]);

  const startKeepAlive = () => {
    const runPing = async () => {
      try {
        const lastPing = localStorage.getItem(KEEP_ALIVE_KEY);
        const now = Date.now();
        if (lastPing && (now - Number(lastPing)) < KEEP_ALIVE_INTERVAL) {
          return;
        }
        await supabase.from('profiles').select('id').limit(1);
        localStorage.setItem(KEEP_ALIVE_KEY, String(now));
      } catch (err) {
        // Silently fail
      }
    };

    runPing();
    const interval = setInterval(runPing, 6 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  };

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const currentUser = session?.user || null;
    setUser(currentUser);
    if (currentUser) {
      await fetchProfile(currentUser.id);
    }
  };

  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (data) setProfile(data);
  };

  const fetchProducts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('display_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error || !data || data.length === 0) {
      setProducts(initialProducts);
    } else {
      setProducts(data);
    }
    setLoading(false);
  };

  const fetchLandingSettings = async () => {
    const { data, error } = await supabase
      .from('landing_settings')
      .select('*')
      .order('setting_key', { ascending: true });
    if (data) setLandingSettings(data);
  };

  const fetchAppSettings = async () => {
    const { data, error } = await supabase
      .from('app_settings')
      .select('*');
    if (data) setAppSettings(data);
  };

  const fetchShippingDistricts = async () => {
    const { data, error } = await supabase
      .from('shipping_districts')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .order('name', { ascending: true });
    if (data) setShippingDistricts(data);
  };

  const getLoadingIconUrl = () => {
    const setting = appSettings?.find(s => s.key === 'loading_icon_url');
    return setting?.value || DEFAULT_LOADING_ICON;
  };

  const isShopComingSoon = () => {
    const setting = appSettings?.find(s => s.key === 'shop_coming_soon');
    return setting?.value === true || setting?.value === 'true';
  };

  // Analytics tracking functions
  const getSessionId = () => {
    let sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }
    return sessionId;
  };

  const trackEvent = async (eventType, data = {}) => {
    try {
      const sessionId = getSessionId();
      await supabase.from('analytics_events').insert({
        event_type: eventType,
        session_id: sessionId,
        page_url: data.page_url || window.location.pathname,
        product_id: data.product_id || null,
        user_id: user?.id || null,
      });
    } catch (err) {
      // Silently fail for analytics - don't break user experience
      console.debug('Analytics tracking failed:', err.message);
    }
  };

  const addToCart = (product) => {
    // Track add to cart event
    trackEvent('add_to_cart', { product_id: product.id });
    
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const updateCartQuantity = (id, quantity) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }
    setCart(prev => prev.map(item => 
      item.id === id ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => setCart([]);
  
  const restoreCart = (items) => setCart(items);

  return (
    <StoreContext.Provider value={{ 
      products, cart, landingSettings, appSettings, shippingDistricts,
      addToCart, removeFromCart, updateCartQuantity, clearCart, restoreCart,
      user, profile, loading, getLoadingIconUrl, isShopComingSoon,
      fetchProducts, fetchLandingSettings, fetchAppSettings, fetchShippingDistricts, checkUser,
      trackEvent
    }}>
      {children}
    </StoreContext.Provider>
  );
};